/**
 * Which phases one pass actually makes.
 *
 * The L120 extrudes the inner and outer sheath TOGETHER — one pass, one drum.
 * Launching that fuses them into a single task: it keeps `phase:
 * 'exterior_sheat'` and carries `tandemPhases: ['interior_sheat',
 * 'exterior_sheat']`.
 *
 * The cost is that `interior_sheat` is then dropped from BOTH `job.phases` and
 * `job.phasesForGant` — which is everything the phase displays read. So a
 * four-phase product started reading `Isolation › Rope › Outer` the moment it
 * went on the L120, with no sign the inner sheath existed, and its phase count
 * quietly fell from 4 to 3 as work began. Only `job.phasePlan` still remembers.
 *
 * The pass stays ONE segment — it is one operation on one machine — but it has
 * to say what it makes, and it has to count for what it makes.
 *
 * The Gantt has always done this (helpers/jobToGantt.js); the two tables had
 * not, so this is shared rather than copied a third time.
 */

/**
 * @param job a job with `tasks`
 * @returns `{ [phaseName]: string[] }` — only phases that fuse others into them
 */
export function fusedPhasesOf(job) {
  const out = {};
  for (const t of Object.values(job?.tasks || {})) {
    // `< 2` on purpose: a "tandem" of one phase fuses nothing, and labelling it
    // "Outer" via a join would be a no-op dressed up as a special case.
    if (!t?.tandem || !Array.isArray(t.tandemPhases) || t.tandemPhases.length < 2) continue;
    const name = t.phase;
    if (!name) continue;
    // Several tasks can share a phase (one per drum); they describe the same
    // fusion, so the first wins.
    if (!out[name]) out[name] = t.tandemPhases;
  }
  return out;
}

/**
 * The label for a phase segment, naming everything the pass makes.
 *
 * @param labelOf how the caller spells a phase — the tables use short forms
 */
export function phaseSegmentLabel(phaseName, fused, labelOf = n => n) {
  const together = fused?.[phaseName];
  return together && together.length > 1
    ? together.map(labelOf).join(' + ')
    : labelOf(phaseName);
}

/** How many phases one segment stands for — 1, unless it is a fused pass. */
export function phaseSegmentCovers(phaseName, fused) {
  const together = fused?.[phaseName];
  return together && together.length > 1 ? together.length : 1;
}
